import { Button, CircularProgress, Grid, TextField } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Router, { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import getUuidByString from 'uuid-by-string';
import ApiService from '../../api/restApi';
import { fetchHeadingAndUrl, _RESOLUTION_ENTITY_TYPE } from '../../utility/common';
import { CREATE_NEW_PAGE_USING, DEFAULT_COUNTRY_LANGAUGE } from '../../constants/content-recommendations';
import { useCustomerInfo, useIsTestDriveUser, useQalert, useTestDriveDomain } from '../../utility/services/customHooks';
import { getHostNameList, validateUrlWithDomains } from '../../utility/validation/urls';

import { Autocomplete } from '@material-ui/lab';
import { isEmpty, isUndefined, uniqBy } from 'lodash';
import { actionTypes } from '../../constants/actionTypes';
import { COMPONENT_NAMES, SCREEN_NAMES, trackAnalyticsEvent } from '../../constants/analyticsMeta';
import { getCountryDetailsByAlpfa2 } from '../../constants/CountryAndLanguages';
import { FormAutoComplete, FormDropDown } from '../formElements';
import { typeAny } from './Content-Brief/contentBriefType';
import { getCRDetailPagePath, getPageDraftDetailPagePath } from '../../utility/services/crListHelper';
import { dispatchMasterDataQuery } from '../../utility/services/api/common';
import { ANNOTATION_SUB_TYPE_VALUE, ANNOTATION_TYPE } from '../../constants/formMeta/recommendation';
import { useUser } from '@auth0/nextjs-auth0/client';
import { format } from 'date-fns';
import CustomAlert from '../shared/CustomAlert';

type propsType = {
  creatingPageFrom: string;
  setShowCreateNewPagePopUp: (params: boolean) => void;
  language_country_details?: typeof DEFAULT_COUNTRY_LANGAUGE;
  copyExistingPageData?: {
    pageId: string;
    pageUrl: string;
    selectedRowDataForPageCreation?: typeAny[];
    selectedSubKeywordsMapping?: { [key: string]: string[] };
    isSelectedKeywordClustersAllProcessed: boolean;
  };
  discoveredKeywords?: string[] | undefined;
  redirectToNewTab?: boolean;
};

interface typeCountryData {
  alpha_2: string;
  alpha_3: string;
  text: string;
  name: string;
  value: string;
}

const DEFAULT_COUNTRY: typeCountryData = {
  name: 'United States of America',
  text: 'United States of America',
  value: 'United States of America',
  alpha_3: 'USA',
  alpha_2: 'US',
};
interface typeLanguageData {
  language_code: string;
  // language_name: string;
  location_code: string | number | null;
  text: string;
  name: string;
  value: string;
}

const DEFAULT_LANGUAGE: typeLanguageData = {
  name: 'English',
  text: 'English',
  value: 'English',
  language_code: 'en',
  location_code: '2840',
};

type DropDownOption = {
  text: string;
  value: string;
  name: string;
};
interface Payload {
  label: string;
  customer_url: string;
  component_name: string;
  screen_name: string;
  step_name?: string; // Optional property
}

const CreateNewPageFromExistingPage: React.FunctionComponent<propsType> = ({
  creatingPageFrom,
  setShowCreateNewPagePopUp,
  language_country_details = { ...DEFAULT_COUNTRY_LANGAUGE },
  copyExistingPageData,
  discoveredKeywords,
  redirectToNewTab = false,
}) => {
  const [countryDataList, setCountryDataList] = useState<typeCountryData[]>([]);
  const [languageDataList, setLanguageDataList] = useState<typeLanguageData[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<typeLanguageData | undefined>();
  const [newPageUrl, setNewPageUrl] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<typeCountryData | undefined>();
  const [heading, setHeading] = useState('');
  const [domainsList, setDomainsList] = useState<string[]>([]);
  // eslint-disable-next-line
  const [existingPageData, setExistingPageData] = useState<any | null>(null);
  const [isFromGrowthTrends, setIsFromGrowthTrends] = useState(false);
  const [validUrlError, setValidUrlError] = useState('');
  const [validLiveUrl, setValidLiveUrl] = useState('');
  const { user } = useUser();
  const [headingError, setHeadingError] = useState('');
  const [countrygError, setCountryError] = useState('');
  const [languageError, setLanguageError] = useState('');
  const [keywordsError, setKeywordsError] = useState('');
  const [keywords, setKeywords] = useState<string[]>(discoveredKeywords || []);
  const [keywordsInputValue, setInputValue] = useState('');

  const [isApiInProcess, setIsApiInProcess] = useState(false);
  const masterLanguageAndCountry: { countryData: any[]; languageData: any[] } | object = useSelector(
    (state) => state.root.masterLanguageAndCountry
  );
  const [domainAutocompleteList, setDomainAutocompleteList] = useState<DropDownOption[] | undefined>();
  const [selectedDomain, setSelectedDomain] = useState('');
  const [showCustomAlert, setShowCustomAlert] = useState(true);

  const [, toggleSnackBar] = useQalert();
  const dispatch = useDispatch();

  const customerInfo = useSelector((state) => state.root.customerInfo) || [];
  const custDetails = useCustomerInfo(customerInfo);

  const isTestDriveUser = useIsTestDriveUser();
  const testDriveDomain = useTestDriveDomain();

  const router = useRouter();

  const getLanguageAndCountryData = useCallback(async () => {
    try {
      const response: { data: any[] } = await ApiService.get(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/master-data/country-language`
      );
      dispatch({
        type: actionTypes.MASTER_COUNTRY_AND_LANGUAGE,
        payload: response.data,
      });
    } catch (error) {
      toggleSnackBar({ type: 'error', text: 'Oops! Something Went Wrong. Please try again.', open: true });
      setShowCreateNewPagePopUp(false);
    }
  }, [dispatch, setShowCreateNewPagePopUp, toggleSnackBar]);

  const fetchDomains = useCallback(
    async (
      domainUrl: string | undefined,
      testDriveDomain: string | undefined,
      isTestDriveUser: boolean | undefined
    ) => {
      try {
        // if it's a test drive, we already have the information in the session
        if (isTestDriveUser) {
          if (testDriveDomain) {
            const domainAutoComplete = [
              {
                value: new URL(testDriveDomain).origin,
                name: testDriveDomain.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
                text: testDriveDomain.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
              },
            ];
            setDomainAutocompleteList(domainAutoComplete);
            setDomainsList([testDriveDomain]);
            setSelectedDomain(domainAutoComplete ? domainAutoComplete[0]?.value || '' : '');
          } else {
            // test drive domain not loaded yet
            setDomainsList([]);
          }
          return;
        }

        // otherwise, for non-test drive users...
        setIsApiInProcess(true);

        const promiseArray = [
          dispatchMasterDataQuery(dispatch, ['domain', 'market_share_segment']),
          ApiService.get(`${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/settings/setup?type=domain`),
        ];

        const [masterDomainResponse, domainSetupResponse] = await Promise.all(promiseArray);

        const allDomains: string[] = [
          ...(getHostNameList(domainSetupResponse?.data?.domainSetup?.SUPPORTED_DOMAINS, 'origin') || ([] as string[])),
        ];

        const domainSetupTextValueMap =
          allDomains?.map((domain) => {
            return {
              value: new URL(domain)?.origin,
              name: domain?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
              text: domain?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
            };
          }) || [];

        const allAutoCompleteDomainList: { text: string; name: string; value: string }[] = [];
        if (masterDomainResponse?.domain?.length) {
          masterDomainResponse?.domain?.forEach((domain) => {
            allDomains.push(new URL(domain?.top_data)?.origin);
            allAutoCompleteDomainList.push({
              value: new URL(domain?.top_data)?.origin,
              name: domain?.top_data.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
              text: domain?.top_data.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
            });
          });
          setSelectedDomain(allAutoCompleteDomainList ? allAutoCompleteDomainList[0]?.value || '' : '');
        } else {
          if (domainUrl) {
            allDomains.push(new URL(domainUrl)?.origin);
            allAutoCompleteDomainList.push({
              value: new URL(domainUrl)?.origin,
              name: domainUrl.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
              text: domainUrl.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').replace(/\/$/, ''),
            });
            setSelectedDomain(allAutoCompleteDomainList ? allAutoCompleteDomainList[0]?.value || '' : '');
          }
        }
        setDomainAutocompleteList([...allAutoCompleteDomainList, ...domainSetupTextValueMap]);
        setDomainsList(allDomains);
      } catch (error) {
        console.error('CreateNewPage:: fetchDomains::error::', error);
      } finally {
        setIsApiInProcess(false);
      }
    },
    []
  );

  const fetchRequiredData = useCallback(
    async (
      domainUrl: string | undefined,
      testDriveDomain: string | undefined,
      isTestDriveUser: boolean | undefined
    ) => {
      setIsApiInProcess(true);
      try {
        const promiseArray = [fetchDomains(domainUrl, testDriveDomain, isTestDriveUser)];
        if (isEmpty(masterLanguageAndCountry)) {
          promiseArray.push(getLanguageAndCountryData());
        }
        await Promise.all(promiseArray);
      } catch (error) {
        console.error('CreateNewPage::fetchRequiredData::error::', error);
      } finally {
        setIsApiInProcess(false);
      }
    },
    [fetchDomains, getLanguageAndCountryData, masterLanguageAndCountry]
  );

  const cancelHandler = () => {
    setShowCreateNewPagePopUp(false);
    trackAnalyticsEvent({
      eventFor: '',
      buttonText: '',
      eventName: 'Create New Page Form Cancel',
      payload: {
        label: 'Create New Page Form Cancel',
        component_name: COMPONENT_NAMES.content_recommendations,
        screen_name: SCREEN_NAMES.content_recommendations,
      },
    });
  };

  useEffect(() => {
    const main = async () => {
      await fetchRequiredData(custDetails?.domain_url, testDriveDomain as string | undefined, isTestDriveUser);

      if (!isEmpty(language_country_details)) {
        const selectedLanguageDetails = {
          name: language_country_details?.language?.name,
          text: language_country_details?.language?.name,
          value: language_country_details?.language?.name,
          language_code: language_country_details?.language?.language_code,
          location_code: language_country_details?.language?.location_code,
        };
        const selectedCountryDetails = {
          name: language_country_details?.country.name,
          text: language_country_details?.country.name,
          value: language_country_details?.country.name,
          alpha_3: language_country_details?.country.alpha_3,
          alpha_2: language_country_details?.country.alpha_2,
        };
        setSelectedLanguage({ ...selectedLanguageDetails });
        setSelectedCountry({ ...selectedCountryDetails });
      }

      if (copyExistingPageData && !isUndefined(copyExistingPageData)) {
        const responseData = fetchHeadingAndUrl(copyExistingPageData, creatingPageFrom);
        if (responseData) {
          setHeading(responseData.pageHeading);
          setNewPageUrl(responseData.pageUrl);
        }
      } else if (
        [
          CREATE_NEW_PAGE_USING.FROM_SEARCH_INTENTS,
          CREATE_NEW_PAGE_USING.FROM_KEYWORD_GAP,
          CREATE_NEW_PAGE_USING.GROWTH_TRENDS,
        ].includes(creatingPageFrom) &&
        discoveredKeywords
      ) {
        const heading = discoveredKeywords[0].charAt(0).toUpperCase() + discoveredKeywords[0].slice(1).toLowerCase();
        const urlEnd = discoveredKeywords[0]?.toLowerCase()?.split(' ')?.join('-');
        setHeading(heading);
        setNewPageUrl(urlEnd);
      }
    };

    main();
  }, []);

  useEffect(() => {
    if (router.pathname?.includes('growth-trends')) {
      setIsFromGrowthTrends(true);
    }
  }, [router.pathname]);

  const formatCountryForDropDown = useCallback((list) => {
    const newList: typeCountryData[] = list.map((ele) => {
      return { name: ele.NAME, text: ele.NAME, value: ele.NAME, alpha_3: ele.ALPHA_3, alpha_2: ele.ALPHA_2 };
    });
    setCountryDataList(newList);
  }, []);

  const formatLanguageForDropDown = useCallback((list) => {
    const newList: typeLanguageData[] = list.map((ele) => {
      return {
        name: ele.LANGUAGE_NAME,
        text: ele.LANGUAGE_NAME,
        value: ele.LANGUAGE_NAME,
        language_code: ele.LANGUAGE_CODE,
        location_code: ele.LOCATION_CODE,
        country_iso_code: ele.COUNTRY_ISO_CODE,
      };
    });
    setLanguageDataList(newList);
  }, []);

  useEffect(() => {
    if (!isEmpty(masterLanguageAndCountry)) {
      if (masterLanguageAndCountry['countryData']) {
        formatCountryForDropDown(masterLanguageAndCountry['countryData']);
      }
      if (masterLanguageAndCountry['languageData']) {
        formatLanguageForDropDown(masterLanguageAndCountry['languageData']);
      }
    }
  }, [formatCountryForDropDown, formatLanguageForDropDown, masterLanguageAndCountry]);

  const uniqueLanguageDataList = useMemo(() => {
    return uniqBy(languageDataList, 'text') || [];
  }, [languageDataList]);

  const uniqueCountryDataList = useMemo(() => {
    return uniqBy(countryDataList, 'alpha_2') || [];
  }, [countryDataList]);

  const findDuplicateUrl = useCallback(async (pageUrl) => {
    try {
      setExistingPageData(null);
      const checkUrlExistOrNot = await ApiService.post(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/master-data/check-url-exist`,
        {
          pageUrl,
        }
      );
      if (checkUrlExistOrNot && checkUrlExistOrNot?.existingRecord) {
        setExistingPageData(checkUrlExistOrNot?.existingRecord);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('CreateNewPageFromExistingPage:findDuplicateUrl:: error', error);
      throw error;
    }
  }, []);

  const redirectHandler = useCallback((record) => {
    // const isNewPage = record?.data_source?.includes('ORG') && record?.is_cnp === 'FALSE' ? '&newPage=true' : '';
    const { params } = record?.is_cnp === 'TRUE' ? getPageDraftDetailPagePath(record) : getCRDetailPagePath('', record);
    window.open(params, '_blank');
  }, []);

  const existingUrlRedirectHandler = () => {
    redirectHandler(existingPageData);
  };

  const liveUrlValidator = async (url) => {
    try {
      const apiResponse = await ApiService.post(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/get-url-live-status`,
        {
          url: url,
        }
      );
      return apiResponse?.data?.url_details?.islive == true;
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const liveUrlRedirect = () => {
    window.open(validLiveUrl);
  };

  const submitFormHandler = async () => {
    setIsApiInProcess(true);
    try {
      let isFormValid = true;
      setHeadingError('');
      setValidUrlError('');
      setCountryError('');
      setLanguageError('');
      setKeywordsError('');
      setValidLiveUrl('');
      if (!heading || !heading.trim()) {
        isFormValid = false;
        setHeadingError('Please enter tentative H1');
      }
      const isHTML = RegExp.prototype.test.bind(/(<([^>]+)>)/i);
      if (isHTML(heading)) {
        isFormValid = false;
        setHeadingError('Please enter a valid tentative H1 or remove the html tags');
      }
      const domains = testDriveDomain ? [testDriveDomain] : [...domainsList];
      let urlSlug = newPageUrl;
      if (urlSlug.startsWith('/')) {
        isFormValid = false;
        setValidUrlError('Please remove / from the beginning of URL slug');
        return;
      } else if (urlSlug.endsWith('/')) {
        urlSlug = urlSlug.replace(/\/+$/, '');
      }
      const pageURL = selectedDomain + '/' + urlSlug;
      //// REMOVEING #_quattr_manual_page from everywhere
      const { isValidUrl } = validateUrlWithDomains(pageURL, domains);
      if (!isValidUrl || !newPageUrl || !newPageUrl.trim()) {
        isFormValid = false;
        setValidUrlError('Please enter a valid URL slug');
      } else if (await findDuplicateUrl(pageURL)) {
        isFormValid = false;
      }
      if (isFormValid && (await liveUrlValidator(pageURL))) {
        isFormValid = false;
        setValidLiveUrl(pageURL);
      }
      if (!selectedCountry || !countryDataList.some((item) => selectedCountry.value === item.value)) {
        isFormValid = false;
        setCountryError('Please select a valid Country');
      }
      if (!selectedLanguage || !languageDataList.some((item) => selectedLanguage.value === item.value)) {
        isFormValid = false;
        setLanguageError('Please select a valid Language');
      }
      if (discoveredKeywords && !keywords?.length) {
        setKeywordsError('Please select at least one keyword');
      }
      if (!isFormValid) {
        return isFormValid;
      } else {
        const newPageId = getUuidByString(custDetails?.cust_code?.replace('_', '') || '', pageURL);
        const dateValue = new Date();

        const finalPayloadKeywords = copyExistingPageData
          ? [
              ...(copyExistingPageData?.selectedRowDataForPageCreation?.map(
                (kwdDet) => kwdDet?.['Keyword Cluster']?.props?.clusterName || kwdDet?.['Keyword Cluster']
              ) || []),
            ]
          : keywords || [];

        trackAnalyticsEvent({
          eventFor: '',
          buttonText: '',
          eventName: `Create New Page Form Submitted`,
          payload: {
            label: `${finalPayloadKeywords.length} Keywords Selected`,
            event_name: `Create New Page Form Submitted`,
            customer_url: copyExistingPageData ? copyExistingPageData?.pageUrl : '',
            component_name: isFromGrowthTrends
              ? COMPONENT_NAMES.growth_trends
              : COMPONENT_NAMES.content_recommendations,
            screen_name: isFromGrowthTrends ? SCREEN_NAMES.growth_trends : SCREEN_NAMES.content_recommendations,
          },
        });

        const locationCodeBasedOnCountry: any = getCountryDetailsByAlpfa2(selectedCountry?.alpha_2);
        const selectedLanguageDetails: any = selectedLanguage ?? {};
        selectedLanguageDetails.location_code =
          locationCodeBasedOnCountry?.location_code ?? selectedLanguageDetails.location_code;

        const payload = {
          copyPage: copyExistingPageData?.pageUrl,
          copyId: copyExistingPageData?.pageId,
          newId: newPageId,
          newPage: pageURL,
          newHeading: heading || '',
          countryAndLanguageDetails: {
            country: { ...selectedCountry, location_code: locationCodeBasedOnCountry?.location_code },
            language: { ...selectedLanguage, location_code: locationCodeBasedOnCountry?.location_code },
          },
          country_code:
            language_country_details?.country?.alpha_3 || (language_country_details?.country?.alpha_2 as string),
          pageUrl: pageURL,
          pageId: newPageId,
          creatingPageFrom: creatingPageFrom,
          annotationPayload: {
            recommendation_id: newPageId,
            reason: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
            date: format(new Date(dateValue), 'MM/dd/yyyy'),
            entity_type: _RESOLUTION_ENTITY_TYPE.CNP,
            entity_id: newPageId,
            annotation_type: ANNOTATION_TYPE.PUBLISHED,
            annotation_subtype: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
            annotation_details: {},
          },
          keywords: finalPayloadKeywords || [],
          subKeywords: copyExistingPageData?.selectedSubKeywordsMapping || [],
          include_serp_info: true,
          isIncluded: true,
        };
        const apiResponse = (await ApiService.post(
          `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/contentbrief/v1/create`,
          payload
        )) as { data: typeAny };

        if (apiResponse?.data?.createdContentdata?.[0]?.page_id === newPageId) {
          const payload: Payload = {
            label: 'New Page Created Successfully',
            customer_url: copyExistingPageData ? copyExistingPageData.pageUrl : '',
            component_name: isFromGrowthTrends
              ? COMPONENT_NAMES.growth_trends
              : COMPONENT_NAMES.content_recommendations,
            screen_name: isFromGrowthTrends
              ? SCREEN_NAMES.growth_trends
              : existingPageData?.is_cnp
              ? SCREEN_NAMES.page_draft
              : SCREEN_NAMES.content_recommendations,
            step_name: existingPageData?.is_cnp ? '3 - Write & Improve Content' : '3 - Improve Content', // Default step_name
          };

          // Remove step_name if isFromGrowthTrends is true
          if (isFromGrowthTrends) {
            delete payload.step_name;
          }

          trackAnalyticsEvent({
            eventFor: '',
            buttonText: '',
            eventName: `New Page Created Successfully`,
            payload: payload,
          });

          if (redirectToNewTab && newPageId) {
            toggleSnackBar({
              type: 'success',
              text: 'New page draft successfully created.',
              open: true,
            });
            // Opens the link in a new tab
            const newTab = window.open(
              `${process.env.NEXT_PUBLIC_HOST_URL}/app/content-recommendations/content-brief/details/${newPageId}`,
              '_blank'
            );
            // Check if the new tab was blocked
            if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
              // If opening in a new tab failed show popup with link to click
              toggleSnackBar({
                type: 'success',
                text: 'New page draft successfully created.',
                open: false,
              });
              dispatch({
                type: actionTypes?.SET_CUSTOM_POPUP_STATUS,
                customPopupConfig: {
                  showPopup: true,
                  text: 'Your browser is blocking pop-ups. Please enable pop-ups & redirects in your browser settings to proceed.',
                  anchorText: 'Click here to open in a new tab',
                  anchorLink: `${process.env.NEXT_PUBLIC_HOST_URL}/app/content-recommendations/content-brief/details/${newPageId}`,
                },
              });
              trackAnalyticsEvent({
                eventName: 'Shown Redirects Blocked Popup',
                payload: {
                  label: 'Shown Redirects Blocked Popup',
                },
              });
            } else if (newTab) {
              trackAnalyticsEvent({
                eventName: 'Viewed Created Page In New Tab',
                payload: {
                  label: 'Viewed Created Page In New Tab',
                },
              });
            }
          } else {
            Router.push(
              `/app/content-recommendations/content-brief/details/[id]`,
              `/app/content-recommendations/content-brief/details/${newPageId}`
            );
          }
          setShowCreateNewPagePopUp(false);
        } else {
          toggleSnackBar({
            type: 'error',
            text: 'We apologize, but a new page couldnt be created due to technical difficulties. Please retry or reach out to us for assistance.',
            open: true,
          });
        }
      }
    } catch (error: any) {
      console.error('CreateNewPageFromExistingPage::submitForm::error', error);
      if (error?.data?.data?.toUpperCase().includes('duplicate'.toUpperCase())) {
        toggleSnackBar({ type: 'error', text: 'URL already exists!!', open: true });
      } else {
        toggleSnackBar({
          type: 'error',
          text: 'We apologize, but a new page couldnt be created due to technical difficulties. Please retry or reach out to us for assistance',
          open: true,
        });
      }
    } finally {
      setIsApiInProcess(false);
    }
  };

  const onInputChangeHandler = (newInputValue: string) => {
    const options = newInputValue.split(',');
    if (options.length > 1 && keywords) {
      setKeywords(
        keywords
          .concat(options)
          .map((x) => x.trim())
          .filter((x) => x)
      );
    } else {
      setInputValue(newInputValue);
    }
  };

  return (
    <>
      <Dialog
        PaperProps={{
          style: {
            width: 500,
            maxWidth: 500,
            borderRadius: '4px',
          },
        }}
        onClose={() => {}}
        aria-labelledby="simple-dialog-title"
        className={''}
        maxWidth="sm"
        open={true}
        data-testid="addNewKeywordDlgBoxWrapper"
      >
        {
          <DialogTitle className="new-keyword-dialog-title" data-testid="addNewKeywordDlgTitle">
            Enter page information
          </DialogTitle>
        }
        <DialogContent className={''} data-testid="addNewKeywordDlgContent">
          <div className="create-new-page-popup">
            <Grid
              container
              style={{ alignItems: 'center', justifyContent: 'start' }}
              className="mb-12"
              data-testid="country-selection"
            >
              <Grid item xs={12} sm={12}>
                <TextField
                  label={`Tentative H1`}
                  fullWidth
                  variant="outlined"
                  value={heading}
                  onChange={(event) => {
                    setHeading(event.target.value);
                  }}
                  name="title_name"
                  error={headingError ? true : false}
                  inputProps={{ style: { borderColor: validUrlError ? 'red' : '' } }}
                />
                {headingError ? <p className="m-0 mt-5 new-page-form-error">{headingError}</p> : null}
              </Grid>
            </Grid>
            {discoveredKeywords && discoveredKeywords.length && (
              <Grid
                container
                style={{ alignItems: 'center', justifyContent: 'start' }}
                className="mt-15 mb-12"
                data-testid="country-selection"
                spacing={1}
              >
                <Grid item xs={12} sm={12}>
                  <Autocomplete
                    data-testid="new-added-chips-button"
                    multiple
                    id="multi-value"
                    options={[]}
                    value={keywords}
                    inputValue={keywordsInputValue}
                    freeSolo
                    onChange={(event, newValue) => {
                      // TODO is there anyway to validate the newValue is valid? or will it never be (string[])[] ?
                      setKeywords(newValue as string[]);
                    }}
                    onInputChange={(event, newInputValue) => {
                      onInputChangeHandler(newInputValue);
                    }}
                    ChipProps={{
                      className: 'multi-chip-setting',
                    }}
                    disableClearable={true}
                    renderInput={(params) => (
                      <>
                        <TextField
                          {...params}
                          className="multiAdd"
                          variant="outlined"
                          label={'Keywords'}
                          fullWidth
                          name="value"
                          // InputProps={{ ...params.InputProps, disableUnderline: true }}
                        />
                      </>
                    )}
                  />
                  {keywordsError ? <p className="m-0 mt-5 new-page-form-error">{keywordsError}</p> : null}
                </Grid>
              </Grid>
            )}
            <Grid
              container
              style={{ alignItems: 'center', justifyContent: 'start' }}
              className="mt-15 mb-12"
              data-testid="country-selection"
            >
              <Grid item xs={6} sm={5} style={{ display: 'flex' }}>
                <FormDropDown
                  className="w-100"
                  fullWidth
                  fullHeight
                  label="Select Domain"
                  options={uniqBy(domainAutocompleteList, 'text') || []}
                  onChange={(res) => {
                    setSelectedDomain(res?.target?.value || '');
                  }}
                  value={selectedDomain || ''}
                  defaultValue={selectedDomain || ''}
                />
                <p style={{ marginLeft: '5px', marginRight: '5px', paddingTop: '5px' }}> / </p>
              </Grid>
              <Grid item xs={6} sm={7}>
                <TextField
                  label={`Tentative URL Slug`}
                  value={newPageUrl}
                  onChange={(event) => {
                    setNewPageUrl(event.target.value);
                  }}
                  error={validUrlError || existingPageData || validLiveUrl ? true : false}
                  inputProps={{
                    style: {
                      textTransform: 'lowercase',
                      borderColor: validUrlError || existingPageData || validLiveUrl ? 'red' : '',
                    },
                  }}
                  fullWidth
                  variant="outlined"
                  name="title_name"
                />
              </Grid>
              {existingPageData ? (
                <p className="m-0 mt-5 new-page-form-error">
                  Page already exists
                  <span className="redirect-click-btn" onClick={existingUrlRedirectHandler}>
                    {' '}
                    click here{' '}
                  </span>
                  to view
                </p>
              ) : validUrlError ? (
                <p className="m-0 mt-5 new-page-form-error">{validUrlError}</p>
              ) : validLiveUrl ? (
                <p className="m-0 mt-5 new-page-form-error">
                  A page already exists on this domain for this URL. Provide a unique URL for your new page. To optimize
                  an existing live page, use the optimize content menu on the left.
                  <span className="redirect-click-btn" onClick={liveUrlRedirect}>
                    {' '}
                    click here{' '}
                  </span>
                  to View
                </p>
              ) : null}
            </Grid>
            <Grid
              container
              style={{ alignItems: 'center', justifyContent: 'start' }}
              className="mt-15 mb-12"
              data-testid="country-selection"
            >
              {/* <TextField
                label="Country"
                disabled
                style={{ background: '#95959514' }}
                variant="outlined"
                fullWidth
                value={language_country_details?.country?.name}
              /> */}
              <FormAutoComplete
                // id={id}
                //disabled={creatingPageFrom === CREATE_NEW_PAGE_USING.FROM_TARGET}
                value={selectedCountry || ''}
                options={uniqueCountryDataList}
                label={'Select Country'}
                className={'mb-25 w-100'}
                onChange={(value) => {
                  setSelectedCountry(value);
                }}
                onTextBlur={(value) => {
                  const findCountry = uniqueCountryDataList?.find(
                    (country) => country?.text === value || country?.value === value
                  ) || {
                    forTempSolu: new Date(),
                    // NOTE:: as once null,undefined,empty set, secondtime Comp is not going re-render due to Virtual DOM check, as first and second time value is same, so do not re-render, to avoid, i have added this solu
                  };
                  // TODO is the above still true after updating the dependencies?
                  setSelectedCountry(findCountry as never);
                }}
                fullWidth
                disableClearable
              />
              {countrygError ? <p className="m-0 mt-5 new-page-form-error">{countrygError}</p> : null}
            </Grid>
            <Grid
              container
              style={{ alignItems: 'center', justifyContent: 'start' }}
              className="mt-15 mb-12"
              data-testid="language-selection"
            >
              {/* <TextField
                label="Language"
                disabled
                style={{ background: '#95959514' }}
                variant="outlined"
                fullWidth
                value={language_country_details?.language?.name}
              /> */}
              <FormAutoComplete
                // id={id}
                //disabled={creatingPageFrom === CREATE_NEW_PAGE_USING.FROM_TARGET}
                value={selectedLanguage || ''}
                options={uniqueLanguageDataList || []}
                label={'Select Language'}
                className={'mb-25 w-100'}
                onChange={(value) => {
                  setSelectedLanguage(value);
                }}
                onTextBlur={(value) => {
                  const findLang = uniqueLanguageDataList?.find(
                    (lang) => lang?.text === value || lang?.value === value
                  ) || {
                    forTempSolu: new Date(), // NOTE:: as once null,undefined,empty set, secondtime Comp is not going re-render due to Virtal DOM check, as first and second time value is same, so do not re-render, to avoid, i have added this solu
                  };
                  // TODO is the above still true after updating the dependencies?
                  setSelectedLanguage(findLang as never);
                }}
                fullWidth
                disableClearable
              />
              {languageError ? <p className="m-0 mt-5 new-page-form-error">{languageError}</p> : null}
            </Grid>
            <Grid container style={{ alignItems: 'center', justifyContent: 'start' }} className="mt-15 mb-12">
              <Grid item xs={12} sm={12} className="align-self-center">
                <div style={{ float: 'right' }}>
                  <span style={{ marginRight: '8px' }}>
                    <Button
                      className={`create-keyword-cancel-btn`}
                      variant="outlined"
                      color="primary"
                      onClick={cancelHandler}
                      style={{ marginTop: '8px' }}
                      data-testid="cancelBtn"
                    >
                      Cancel
                    </Button>
                    <Button
                      className={`create-intent-cta`}
                      variant="contained"
                      color="primary"
                      onClick={submitFormHandler}
                      style={{ marginTop: '8px', textTransform: 'capitalize', boxShadow: 'none' }}
                      data-testid="addpageBtn"
                      disabled={isApiInProcess}
                    >
                      {isApiInProcess ? <CircularProgress className="position-abs" size={20} /> : null}
                      Create New Page
                    </Button>
                  </span>
                </div>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(CreateNewPageFromExistingPage);
